<template>
  <div
    v-if='!isMobile'
    class='storages-filters'
  >
    <DropdownFilter
      v-for='(filter, index) in topFilters'
      :key='index'
      v-bind='filter'
      :search-i18n-t='tpSearch'
      @filter:update='syncFilter(filters.indexOf(filter), $event)'
      @filter:reset='resetFilter(filters.indexOf(filter))'
    />
  </div>
  <div
    ref='rootRef'
    class='outer-height'
  >
    <div class='inner'>
      <Transition v-if='!isMobile' :duration='500'>
        <StoragesShow
          v-if='storageId'
          class='storages-show'
          :storage='activeStorage'
          :boxes='activeStorageBoxes'
          :all-box-sizes='allBoxSizes'
          :graphql-url='graphqlUrl'
          :lead-props='leadProps'
          @back:click='switchToStoragesIndex'
          @boxes:fetch='updateBoxesStore'
        />
      </Transition>
      <Transition :duration='isMobile ? 0 : 500'>
        <StoragesIndex
          v-show='isMobile || !storageId'
          ref='storagesIndexRef'
          class='storages-index'
          :filtered-visible-storages='filteredVisibleStorages'
          :size-filter-options='sizeFilterOptions'
          :size-filter='sizeFilter'
          :filter-index='filters.indexOf(sizeFilter)'
          :is-mobile-map-active='isMobileMap && isMobile'
          :selected-city-name='selectedCityName'
          @storage:click='switchToStoragesShow'
          @toggle:size-filter='toggleSizeFilter'
          @filters:reset='resetFilters'
        >
          <template v-if='isMobile'>
            <LabelButton
              :class='{ "is-some-filters-selected": isSomeFiltersSelected }'
              :is-active='isFiltersModal'
              @click='toggleFiltersModal(true)'
            >
              {{ tp('mobile_filters') }}
            </LabelButton>
          </template>
        </StoragesIndex>
      </Transition>
    </div>
    <Map
      v-if='!isMobile || isMobileMap'
      :data-node='mapDataNode'
      :all-storages='storages'
      :filtered-storages='filteredStorages'
      :active-storage='activeStorage'
      :map-type='mapType'
      :lead-props='leadProps'
      @bounds:change='boundsChange'
      @balloon:open='switchToStoragesShow'
      @balloon:hide='switchToStoragesIndex'
    />
    <Transition :duration='500'>
      <StoragesShow
        v-if='storageId && isMobile && isMobileMap'
        class='storages-show'
        :storage='activeStorage'
        :boxes='activeStorageBoxes'
        :all-box-sizes='allBoxSizes'
        :container-node='rootRef'
        :graphql-url='graphqlUrl'
        :lead-props='leadProps'
        @back:click='switchToStoragesIndex'
        @boxes:fetch='updateBoxesStore'
      />
    </Transition>
  </div>

  <MapSwitcher
    v-if='isMobile'
    :is-map-active='isMobileMap'
    i18n-key='frontend.storages_map-v4.map_switcher'
    @toggle-map='toggleMobileMap'
  />

  <Modal
    v-if='isMobile'
    :is-active='isFiltersModal'
    :is-slide-down-handle='false'
    css-class='b-filters-modal'
    @modal:hide='toggleFiltersModal(false)'
  >
    <FiltersModal
      :filters='topFilters'
      :storages-count='filteredVisibleStorages.length'
      @filter:update='syncFilter($event.filterIndex, $event.attributes)'
      @filters:reset='resetFilters'
      @modal:hide='toggleFiltersModal(false)'
    />
  </Modal>
</template>

<script>
export default {
  name: 'StoragesMapV4'
};
</script>

<script setup>
import {
  ref,
  computed,
  watch,
  onBeforeMount,
  onBeforeUnmount,
  nextTick
} from 'vue';
import queryString from 'query-string';
import Cookies from 'js-cookie';

import compact from 'lodash/compact';
import filter from 'lodash/filter';
import flatMap from 'lodash/flatMap';
import flatten from 'lodash/flatten';
import map from 'lodash/map';
import reject from 'lodash/reject';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';

import { useMobileDetect } from '../composables/mobile_detect';

import DropdownFilter from '../components/dropdown_filter/index';
import LabelButton from '../components/label_button';
import Modal from '../components/modal';
import FiltersModal from './filters_modal';
import Scrollable from './scrollable';
import Map from './map';
import StoragesShow from './storages_show';
import StoragesIndex from './storages_index';
import MapSwitcher from '../storage_schema_boxes/map_switcher';

import makeVueModal from '@/views/modal/make_vue_modal';
import track from '@/helpers/track';

import { t, prepared } from '@/helpers/i18n';
import SweetScroll from '@/helpers/sweet_scroll';
import isVisibleInViewport from '@/helpers/is_visible_in_viewport';

const props = defineProps({
  mapType: {
    type: String,
    required: true,
    validator: value => ['ymap', 'gmap'].indexOf(value) !== -1
  },
  storages: { type: Array, required: true },
  mapDataNode: { type: Node, required: true },
  presetFilterOptions: { type: Array, required: true },
  isDistricts: { type: Boolean, required: true },
  allBoxSizes: { type: Array, required: true },
  leadProps: { type: Object, required: true },
  graphqlUrl: { type: String, required: true }
});
const tp = prepared('frontend.storages_map-v4.index');
const tpSearch = prepared('frontend.storages_map-v4.dropdown_filter');
const { isMobile } = useMobileDetect();

const storagesIndexRef = ref(null);
const rootRef = ref(null);

const isFiltersModal = ref(false);
const isMobileMap = ref(true);
const storageId = ref(null);

const boxesStore = ref({});

const activeStorage = computed(() => (
  storageId.value ?
    props.storages.find(({ id }) => id === storageId.value) :
    undefined
));
const activeStorageBoxes = computed(() => (
  activeStorage.value ?
    boxesStore.value[activeStorage.value.id] :
    undefined
));

onBeforeMount(() => {
  setFilters(extractQueryParams());
  if (activeStorage.value === undefined) {
    track({ event: 'view_storages' });
  } else {
    track({ event: 'view_storage' });
  }
});

const cities = props.storages
  |> flatMap(?, storage => storage.cities)
  |> sortBy(?, 'index')
  |> uniqBy(?, 'name');

const districts = props.isDistricts ?
  uniq(
    flatten(
      props.storages.map(storage => storage.districts)
    )
  ).sort() :
  [];

const sizes = props.storages
  |> flatMap(?, storage => storage.sizes.filter(size => size.isAvailable))
  |> sortBy(?, 'index')
  |> map(?, 'name')
  |> uniq;

const allowedFeatures = tp('features_v2');
const featuresV2 = props.storages
  |> flatMap(?, storage => storage.featuresV2)
  |> uniq
  |> filter(?, kind => allowedFeatures[kind])
  |> map(?, kind => [kind, allowedFeatures[kind]])
  |> reject(?, ([_kind, text]) => text.includes('%{'))
  |> sortBy(?, [
    ([_kind, text]) => {
      const code = text.charCodeAt(0);
      // return false for russian (to put it first), true for non-russian
      return code < 0x0400 || code > 0x04FF;
    },
    // second sort criterion: alphabetical within each group
    ([_kind, text]) => text
  ]);
const FEATURES_SYNONYMS = {
  freight_elevator: 'freight_passenger_elevator',
  passenger_elevator: 'freight_passenger_elevator',
  access_via_app: 'access_by_pin_code_and_via_app',
  access_by_pin_code: 'access_by_pin_code_and_via_app',
  vehicle_entry_up_to_N_meters: 'vehicle_entry'
};

const metros = props.storages
  |> flatMap(?, storage => storage.metros)
  |> uniqBy(?, 'name')
  |> sortBy(?, 'name');

const storages = props.storages
  |> sortBy(?, 'name');

const PLACEMENT_TOP = 'top';
const PLACEMENT_ASIDE = 'right';

const TUMEN = 'Тюмень';
const isTumen = cities.filter(city => city.name === TUMEN).length > 0;

const filters = ref(compact([{
  name: 'city',
  label: tp('filters.city'),
  options: cities.map(city => ({
    value: city.name,
    label: city.name,
    isChecked: false,
    isNew: city.isNew
  })),
  isActive: false,
  isCheckedByDefault: true,
  isCity: true,
  isMetro: false,
  isRadio: true,
  isSearch: false,
  isSingleLabel: true,
  testLogic: 'some',
  test(storage, filter, option) {
    return !!storage.cities.find(({ name }) => name === option.value);
  },
  location: PLACEMENT_TOP
}, (districts.length ?
  {
    name: 'district',
    label: tp('filters.district'),
    options: districts.map(value => ({
      value,
      label: value,
      isChecked: false
    })),
    isActive: false,
    isCheckedByDefault: true,
    isCity: false,
    isMetro: false,
    isRadio: false,
    isSearch: false,
    isSingleLabel: true,
    testLogic: 'some',
    test(storage, filter, option) {
      return storage.districts.includes(option.value);
    },
    location: PLACEMENT_TOP
  } :
  null), {
  name: 'options',
  label: tp('filters.features'),
  options: featuresV2.map(([value, label]) => ({
    value,
    label,
    isChecked: false
  })),
  isActive: false,
  isCheckedByDefault: false,
  isCity: false,
  isMetro: false,
  isRadio: false,
  isSearch: false,
  testLogic: 'every',
  test(storage, filter, option) {
    return storage.featuresV2.some(feature => (
      feature === option.value || (
        FEATURES_SYNONYMS[option.value] && feature === FEATURES_SYNONYMS[option.value]
      )
    ));
  },
  location: PLACEMENT_TOP
}, (metros.length ?
  {
    name: 'metro',
    label: tp('filters.metro'),
    options: metros.map(metro => ({
      value: String(metro.id),
      label: metro.name,
      markerClass: 'metro-line-marker',
      markerStyle: { background: metro.color },
      isChecked: false
    })),
    isActive: false,
    isCheckedByDefault: false,
    isCity: false,
    isMetro: true,
    isRadio: false,
    isSearch: true,
    testLogic: 'some',
    test(storage, filter, option) {
      return storage.metros.some(metro => String(metro.id) === option.value);
    },
    location: PLACEMENT_TOP
  } :
  undefined), {
  name: 'storages',
  label: tp('filters.storages'),
  options: storages.map(storage => ({
    value: String(storage.id),
    label: storage.name,
    discount: storage.discount,
    isChecked: false
  })),
  isActive: false,
  isCheckedByDefault: false,
  isCity: false,
  isMetro: false,
  isRadio: false,
  isSearch: true,
  testLogic: 'some',
  test(storage, filter, option) {
    return String(storage.id) === option.value;
  },
  location: PLACEMENT_TOP
}, {
  name: 'sizes',
  label: tp('filters.sizes'),
  options: props.allBoxSizes.map(value => ({
    value,
    label: t(`frontend.storage_schema_boxes.index.sizes.${value}.title`),
    hint: t(`frontend.storage_schema_boxes.index.sizes.${value}.subtitle`),
    isChecked: false,
    isDisabled: !sizes.includes(value)
  })),
  isActive: false,
  isCheckedByDefault: false,
  isCity: false,
  isMetro: false,
  isRadio: false,
  isSearch: false,
  testLogic: 'some',
  test(storage, filter, option) {
    return storage.sizes
      .filter(size => size.isAvailable)
      .some(size => size.name === option.value);
  },
  location: PLACEMENT_ASIDE
}]));

const topFilters = computed(() => {
  const filteredFilters = filters.value.filter(({ location }) => location === PLACEMENT_TOP);

  if (selectedCityName.value === TUMEN) {
    return filteredFilters.filter(({ isMetro }) => !isMetro);
  } else {
    return filteredFilters;
  }
});

const selectedCityName = computed(() => (
  filters.value
    .find(({ isCity }) => isCity)
    .options
    .find(({ isChecked }) => isChecked)
    ?.value
));

const notSizeFilters = computed(() => (
  filters.value.filter(filter => filter.location !== PLACEMENT_ASIDE)
));

const sizeFilter = computed(() => (
  filters.value.find(({ location }) => location === PLACEMENT_ASIDE)
));
const sizeFilterOptions = computed(() => (
  sizeFilter.value.options.map(option => (
    {
      ...option,
      isDisabled: !option.isChecked &&
        (option.isDisabled || !filteredNotSizeFilterStorages.value.find(storage => (
          storage.sizes.find(({ isAvailable, name }) => (
            isAvailable && name === option.value
          ))
        ))
        )
    }
  ))
));

const visibleStorages = ref(props.storages);
const filteredNotSizeFilterStorages = computed(() => (
  props.storages.filter(storage => (
    notSizeFilters.value.every(filter => testFilter(storage, filter))
  ))
));
const filteredStorages = computed(() => (
  filteredNotSizeFilterStorages.value.filter(storage => (
    testFilter(storage, sizeFilter.value)
  ))
));
const filteredVisibleStorages = computed(() => (
  filteredStorages.value.filter(storage => (
    visibleStorages.value.includes(storage)
  ))
));

const isSomeFiltersSelected = computed(() => (
  topFilters.value
    .reduce((memo, filter) => (
      memo + filter.options.filter(({ isChecked }) => isChecked).length
    ), 0) > 1
));

function testFilter(storage, filter) {
  const activeOptions = filter.options.filter(option => option.isChecked);
  if (!activeOptions.length || activeOptions.length === filter.options.length) {
    return true;
  }

  return activeOptions[filter.testLogic](option => filter.test(storage, filter, option));
}

function syncFilter(filterIndex, attributes) {
  Object.assign(filters.value[filterIndex], attributes);

  // save url when it is not isActive changed
  if (attributes.isActive === undefined) {
    saveUrlLocation();
  }
}

function toggleSizeFilter(filterIndex, option) {
  if (option.isDisabled) return;

  const isChecked = !option.isChecked;
  const filter = filters.value[filterIndex];
  const index = filter.options.findIndex(opt => opt.value === option.value);

  syncFilter(filterIndex, {
    options: filter.options.map((opt, i) => (
      index === i ? { ...opt, isChecked } : opt
    ))
  });
}

function resetFilter(filterIndex) {
  const filter = filters.value[filterIndex];
  Object.assign(filter, { isActive: false });

  const queryParams = extractQueryParams();
  delete queryParams[filter.name];

  setFilters(queryParams);
  saveUrlLocation();
}

function extractQueryParams() {
  return queryString.parse(window.location.href.replace(/.*?(?:\?|$)/, ''));
}

function setFilters(queryParams) {
  const preparedPresetFilterOptions = props.presetFilterOptions.reduce((memo, object) => {
    memo[object.filter] = object.option;
    return memo;
  }, {});

  filters.value.forEach(filter => {
    filter.options.forEach(option => option.isChecked = false);

    (queryParams[filter.name] ||
      preparedPresetFilterOptions[filter.name] ||
      ''
    )
      .split(',')
      .forEach(activeOptionName => {
        const activeOption = filter.options.find(v => v.value === activeOptionName);
        if (activeOption) {
          activeOption.isChecked = true;
        }
      });

    if (filter.isCheckedByDefault && filter.options.every(v => !v.isChecked)) {
      if (filter.isRadio) {
        // can be no option[0] when running spacehubstore on russian dev
        filter.options[0]?.isChecked = true;
      } else {
        filter.options.forEach(option => option.isChecked = true);
      }
    }
  });

  storageId.value = parseInt(queryParams.storageId);
}

function resetFilters() {
  setFilters({});
  saveUrlLocation();
}

function saveUrlLocation() {
  const mappedFilters = compact(
    filters.value.map(filter => {
      let activeOptions = filter.options
        .filter(option => option.isChecked)
        .map(option => option.value);

      if (filter.isCheckedByDefault &&
          activeOptions.length === filter.options.length) {
        activeOptions = [];
      }

      return activeOptions.length ?
        `${filter.name}=${activeOptions.join(',')}` :
        null;
    })
  );
  if (storageId.value) {
    mappedFilters.push(`storageId=${storageId.value}`);
  }

  const url = location.href.replace(/\?.*/, '') +
    (mappedFilters.length ? '?' : '') +
    mappedFilters.join('&');

  window.history.replaceState({ turbolinks: true, url }, '', url);
}

function boundsChange(storages) {
  visibleStorages.value = storages;
}

async function toggleMobileMap() {
  isMobileMap.value = !isMobileMap.value;

  // headline in storages_map
  const focusNode = rootRef.value.parentNode.previousSibling;

  if (focusNode && !isVisibleInViewport(focusNode, true)) {
    (await SweetScroll.asyncInstance()).toElement(
      focusNode, { duration: 750, offset: -10 }
    );
  }
}

function switchToStoragesIndex() {
  storageId.value = null;
  track({ event: 'view_storages' });

  saveUrlLocation();

  nextTick(() => {
    if (storagesIndexRef.value) {
      storagesIndexRef.value.restoreScrollPosition();
    }
  });
}

function switchToStoragesShow(id) {
  storageId.value = id;
  track({ event: 'view_storage' });

  saveUrlLocation();

  if (storagesIndexRef.value) {
    storagesIndexRef.value.saveScrollPosition();
  }
}

function updateBoxesStore({ storageId, boxes }) {
  boxesStore.value[storageId] = boxes;
}

function toggleFiltersModal(isActive) {
  isFiltersModal.value = isActive;
}

watch(() => filteredVisibleStorages.value.length, () => {
  if (storagesIndexRef.value) {
    storagesIndexRef.value.resetScroll();
  }
});
</script>

<style scoped lang='sass'>
@import 'app/packs/stylesheets/functions/rem'
@import 'app/packs/stylesheets/mixins/margins'
@import 'app/packs/stylesheets/mixins/container'
@import 'app/packs/stylesheets/mixins/responsive'

.storages-filters
  +gte_laptop
    +container_desktop
    display: flex
    gap: 12px
    margin-bottom: var(--article-headline-gap)

  ::v-deep(.dropdown-filter .inner-background)
    min-width: 270px

  ::v-deep(.dropdown-filter.metro)
    .content
      flex-grow: 1

::v-deep(.metro-line-marker)
  border-radius: 100%
  margin-left: auto

  +lte_ipad
    height: rem4(8px)
    width: rem4(8px)

  +gte_laptop
    height: 8px
    width: 8px

.outer-height
  display: flex

  +lte_ipad
    --section-gap: #{rem4(16px)}
    $header-height: 110px
    $map-height: 592px
    flex-direction: column
    gap: rem4(16px)
    min-height: min(90vh, #{rem4($header-height + $map-height)})
    //min-height: min(calc(80vh + #{rem4($header-height)}), #{rem4($header-height + $map-height)})
    position: relative

  +gte_laptop
    --content-above-map-height: 404px
    --section-gap: 24px
    height: max(687px, calc(100vh - var(--content-above-map-height)))
    box-shadow: 0px 0px 24.944px 0px rgba(3, 3, 3, 0.10)

  .inner
    +lte_ipad
      flex-grow: 1
      width: 100%
      display: flex
      flex-direction: column

    +gte_laptop
      flex-shrink: 0
      height: 100%
      position: relative
      width: 456px

.is-some-filters-selected
  &:after
    background: #ff4f00
    border-radius: rem4(6px)
    content: ''
    height: rem4(6px)
    width: rem4(6px)

.storages-show,
.storages-index
  display: flex
  flex-direction: column
  padding: var(--section-gap) var(--section-gap) 0 var(--section-gap)
  gap: var(--section-gap)

  +gte_laptop
    height: 100%
    width: inherit

.storages-index
  +lte_ipad
    flex-grow: 1
    padding-top: 0
    padding-left: 0
    padding-right: 0

    ::v-deep(.scrollable)
      padding-left: var(--section-gap)
      padding-right: var(--section-gap)

// animations
+lte_ipad
  .storages-show
    &.v-enter-active,
    &.v-leave-active
      transition: all 500ms

    &.v-enter-active
      transform: translateX(-100%)
    &.v-enter-to
      transform: translateX(0%)

    &.v-leave-active
      transform: translateX(0%)
    &.v-leave-to
      transform: translateX(-100%)

+gte_laptop
  .storages-show,
  .storages-index
    &.v-enter-active,
    &.v-leave-active
      left: 0
      position: absolute
      top: 0
      transition: all 500ms

  .storages-show
    &.v-enter-active
      transform: translateX(100%)
    &.v-enter-to
      transform: translateX(0%)

    &.v-leave-active
      transform: translateX(0%)
    &.v-leave-to
      transform: translateX(100%)

  .storages-index
    &.v-enter-active
      transform: translateX(-100%)
    &.v-enter-to
      transform: translateX(0%)

    &.v-leave-active
      transform: translateX(0%)
    &.v-leave-to
      transform: translateX(-100%)
</style>

<style lang='sass'>
@import 'app/packs/stylesheets/functions/rem'
@import 'app/packs/stylesheets/mixins/ltr_rtl'
@import 'app/packs/stylesheets/mixins/responsive'

.b-filters-modal.b-modal
  --max-height: calc(var(--vh, 1vh)*100)

  &.is-active .modal-inner
    display: flex
    flex-direction: column

  .modal-inner
    height: 100%

  .content-wrapper
    flex-grow: 1
    display: flex
    flex-direction: column
</style>
